import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import axiosClient from "../../api/axiosClient";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";


export const getApiKeys = async (page = 1, pageSize = 20) => {
  const response = await axiosClient.get("/manage/api-keys", {
    params: { page, pageSize },
  });
  return response.data as {
    apiKeys: {
      id: number;
      name: string;
      rateLimit: number;
      createdAt: string;
    }[];
    page: number;
    pageSize: number;
    total: number;
  };
};

export const deleteApiKey = async (keyId: string, idempotencyKey: string) => {
  const response = await axiosClient.delete(`/manage/api-keys/${keyId}`, {
    headers: { "Idempotency-Key": idempotencyKey },
  });
  return response.data;
};


async function createApiKey(name: string, rateLimit: number, idempotencyKey: string) {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Token manquant");

  const res = await axios.post(
    "https://staging-third-party-apis.nabil5332.workers.dev/api/v1/manage/api-keys",
    { name, rateLimit },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "Idempotency-Key": idempotencyKey,
      },
    }
  );

  return res.data;
}


export interface ApiKey {
  id: number;
  name: string;
  rateLimit: number;
  createdAt: string;
}


const ManagesView: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [newApiKey, setNewApiKey] = useState<{ name: string; rateLimit: string | number }>({
    name: "",
    rateLimit: "",
  });


  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);


  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedKeyId, setSelectedKeyId] = useState<number | null>(null);

  const handleDeleteClick = (id: number) => {
    setSelectedKeyId(id);
    setOpenConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedKeyId === null) return;

    try {
      const idempotencyKey = uuidv4();
      await deleteApiKey(String(selectedKeyId), idempotencyKey);
      await fetchKeys();
    } catch (err) {
      console.error("Erreur lors de la suppression :", err);
    } finally {
      setOpenConfirm(false);
      setSelectedKeyId(null);
    }
  };

  const handleCancelDelete = () => {
    setOpenConfirm(false);
    setSelectedKeyId(null);
  };

  const fetchKeys = async () => {
    try {
      setLoading(true);
      const data = await getApiKeys(page + 1, rowsPerPage);
      setApiKeys(data.apiKeys || []);
      setTotal(typeof data.total === "number" ? data.total : (data.apiKeys || []).length);
    } catch (err) {
      console.error("Erreur lors du chargement des clés API :", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
  }, [page, rowsPerPage]);

  const handleAddApiKey = async () => {
    if (!newApiKey.name || newApiKey.rateLimit === "" || Number(newApiKey.rateLimit) <= 0) return;

    try {
      const idempotencyKey = uuidv4();
      const createdKey = await createApiKey(
        newApiKey.name,
        Number(newApiKey.rateLimit),
        idempotencyKey
      );

      localStorage.setItem("apiKey", createdKey.apiKey);

      setPage(0);
      setNewApiKey({ name: "", rateLimit: "" });
      await fetchKeys();
    } catch (err) {
      console.error("Erreur lors de la création :", err);
    }
  };



  // --- Pagination ---
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box p={4}>
      <h1 style={{ color: "#714291", marginBottom: "1rem" }}>Management - API Keys</h1>

      <Stack direction="row" spacing={2} mb={4}>
        <TextField
          label="Name"
          value={newApiKey.name}
          onChange={(e) => setNewApiKey({ ...newApiKey, name: e.target.value })}
          size="small"
        />
        <TextField
          label="Rate Limit"
          type="number"
          value={newApiKey.rateLimit}
          onChange={(e) =>
            setNewApiKey({
              ...newApiKey,
              rateLimit: e.target.value === "" ? "" : Number(e.target.value),
            })
          }
          size="small"
        />

        <Button
          variant="contained"
          onClick={handleAddApiKey}
          sx={{ backgroundColor: "#714291", "&:hover": { backgroundColor: "#5a336f" } }}
        >
          Add API Key
        </Button>
      </Stack>


      <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Table>
          <TableHead sx={{ backgroundColor: "#714291" }}>
            <TableRow>
              <TableCell sx={{ color: "#fff" }}>Name</TableCell>
              <TableCell sx={{ color: "#fff" }}>Rate Limit</TableCell>
              <TableCell sx={{ color: "#fff" }}>Created At</TableCell>
              <TableCell sx={{ color: "#fff", textAlign: "center" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Chargement...
                </TableCell>
              </TableRow>
            ) : apiKeys.length > 0 ? (
              apiKeys.map((key) => (
                <TableRow key={key.id} hover>
                  <TableCell>{key.name}</TableCell>
                  <TableCell>{key.rateLimit}</TableCell>
                  <TableCell>{new Date(key.createdAt).toLocaleString()}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      onClick={() => handleDeleteClick(key.id)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Aucune donnée trouvée
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 20, 50]}
      />

      <Dialog
        open={openConfirm}
        onClose={handleCancelDelete}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            Êtes-vous sûr de vouloir supprimer cette API Key ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Annuler
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManagesView;
